
import {
    computed,
    defineComponent,
    onMounted,
    onUnmounted,
    PropType,
    reactive,
    Ref,
    ref,
    toRaw,
    watch,
    nextTick,
} from 'vue'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import OkInput from '@/modules/OktellModule/components/OkInput/index.vue'
import OkButton from '@/modules/OktellModule/components/OkButton/index.vue'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useValidationMessages from '@/common/composable/useValidationMessages'

import { useOnOutsidePress } from 'vue-composable'

// types
import { ErrorObject } from '@vuelidate/core'

export default defineComponent({
    components: {
        loader: Loader,
        'ok-input': OkInput,
        'ok-button': OkButton,
    },
    props: {
        name: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, Array, null, Number, String, Boolean] as PropType<any | any[]>,
            required: true,
        },
        multiselect: {
            type: Boolean,
            default: false,
        },
        selectBy: {
            type: String,
            default: 'id',
        },
        searchBy: {
            type: String,
            default: 'name',
        },
        label: {
            type: String,
            default: 'name',
        },
        placeholder: {
            type: String,
            default: 'Выберите значение',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
        options: {
            type: Array as PropType<any[]>,
            default: () => [],
        },
        isInvalid: {
            type: Boolean,
            default: false,
        },
        validationErrors: {
            type: Array as PropType<ErrorObject[]>,
            default: () => [],
        },
        canDeselect: {
            type: Boolean,
            default: false,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        failed: {
            type: Boolean,
            default: false,
        },
        success: {
            type: Boolean,
            default: false,
        },
        errorText: {
            type: String,
            default: null,
        },
        infiniteScroll: {
            type: Boolean,
            default: false,
        },
        searchWithoutRefresh: {
            type: Boolean,
            default: false,
        },
        emptyText: {
            type: String,
            default: 'Список пуст',
        },
    },
    emits: [
        'update:value',
        'change',
        'search',
        'refresh',
        'loadmore',
        'option-by-search',
        'last-value',
    ],
    setup(props, { emit }) {
        const elementref: Ref<HTMLElement | null> = ref(null)

        const state = reactive({
            is_opened: false,
            items: [] as any[],
            search_query: null as string | null,
            search_by_api: false,
        })

        const { getValidationMessage } = useValidationMessages()

        watch(
            () => props.options,
            (newest) => {
                state.items = newest
            }
        )

        const toggleOptions = async () => {
            if (!props.disabled) state.is_opened = !state.is_opened

            // autofocus search if is opened
            if (state.is_opened && props.searchable) {
                if (!elementref.value) return

                const inputElement = elementref.value.getElementsByTagName(
                    'INPUT'
                )[0] as HTMLElement | null

                if (!inputElement) return

                await nextTick()
                inputElement.focus()
            }
        }

        const closeOptions = () => {
            if (state.is_opened) state.is_opened = false
        }

        useOnOutsidePress(elementref, closeOptions)

        const selectedOption = computed(() => {
            if (props.multiselect) {
                return null
            }

            if (props.value) {
                return props.options.find((option) => option[props.selectBy] === props.value)
            }

            return null
        })

        const selectedOptions = computed(() => {
            if (!props.multiselect) return []

            return props.options.filter((option) => {
                if (!props.value || !Array.isArray(props.value)) return
                return props.value.includes(option[props.selectBy])
            })
        })

        const emitSelect = (option: any) => {
            if (props.multiselect) {
                state.search_query = null
                state.items = props.options

                if (Array.isArray(props.value)) {
                    const values = props.value

                    if (values.includes(option)) {
                        const index = values.findIndex((value) => value == option)
                        values.splice(index, 1)
                    } else {
                        values.push(option)
                    }

                    emit('update:value', values)
                    emit('change', values)
                    emit('last-value', option)
                }

                return
            }

            if (props.value === option) {
                if (props.canDeselect) {
                    emit('update:value', null)
                    emit('change', null)
                    state.search_query = null
                    state.items = props.options
                    return
                }

                return
            }

            emit('update:value', option)
            emit('change', option)

            state.search_query = null
            state.items = props.options

            if (state.search_by_api) {
                const { findObjectByValue } = useHelpers()
                const selected_option = findObjectByValue(props.selectBy, option, props.options)
                emit('option-by-search', toRaw(selected_option))
                state.search_by_api = false
            }

            if (props.closeOnSelect) {
                toggleOptions()
            }
        }

        const searchOptionByApi = (query: string) => {
            emit('search', query)
            state.search_by_api = true
        }

        const searchOption = () => {
            const query = state.search_query?.toLowerCase()

            if (query) {
                state.items = props.options.filter((option) => {
                    return String(option[props.searchBy]).toLowerCase().includes(query)
                })

                if (!state.items.length) {
                    searchOptionByApi(query)
                }
            } else {
                if (state.search_by_api && !props.searchWithoutRefresh) {
                    emit('refresh')
                } else {
                    state.items = props.options
                }
            }
        }

        const emitRefresh = () => {
            emit('refresh')
        }

        // inifinite scroll
        const optionswindow: Ref<HTMLElement | null> = ref(null)

        const handleScroll = () => {
            if (!optionswindow.value || props.loading) return

            const scrollHeight = Math.floor(optionswindow.value.scrollHeight)
            const scrollTop = Math.floor(optionswindow.value.scrollTop)
            const offsetHeight = Math.floor(optionswindow.value.offsetHeight)

            if (scrollTop >= scrollHeight - offsetHeight) {
                emit('loadmore')
            }
        }

        onMounted(() => {
            state.items = props.options
            if (!optionswindow.value || !props.infiniteScroll) return
            optionswindow.value.addEventListener('scroll', handleScroll)
        })

        onUnmounted(() => {
            if (!optionswindow.value || !props.infiniteScroll) return
            optionswindow.value.removeEventListener('scroll', handleScroll)
        })

        return {
            state,
            emitSelect,
            toggleOptions,
            selectedOption,
            selectedOptions,
            searchOption,
            emitRefresh,
            optionswindow,
            elementref,
            getValidationMessage,
        }
    },
})
